import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCampaigns } from '../hooks/useCampaigns';
import './CampaignList.css';
import {
  shouldWarnInactiveTemplate,
  INACTIVE_TEMPLATE_MESSAGE
} from '../utils/campaignTemplate';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'sending', label: 'Sending' },
  { value: 'sent', label: 'Sent' },
  { value: 'paused', label: 'Paused' },
  { value: 'canceled', label: 'Canceled' }
];

function CampaignList() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useCampaigns({ status, search, page, limit: 20 });

  const getStatusBadgeClass = (status) => {
    const classMap = {
      draft: 'badge-gray',
      scheduled: 'badge-blue',
      sending: 'badge-yellow-pulse',
      sent: 'badge-green',
      paused: 'badge-orange',
      canceled: 'badge-red'
    };
    return `status-badge ${classMap[status] || 'badge-gray'}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (error) {
    return (
      <div className="campaign-list">
        <div className="error-message">Failed to load campaigns: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="campaign-list">
      <header className="page-header">
        <h1>Campaigns</h1>
        <button className="btn-primary" onClick={() => navigate('/campaigns/new')}>
          + New Campaign
        </button>
      </header>

      <div className="filter-bar">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="filter-select"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search campaigns..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="filter-search"
        />
      </div>

      {isLoading ? (
        <div className="loading-message">Loading campaigns...</div>
      ) : !data || data.data.length === 0 ? (
        <div className="empty-state">
          <p>No campaigns found</p>
          <button className="btn-secondary" onClick={() => navigate('/campaigns/new')}>
            Create your first campaign
          </button>
        </div>
      ) : (
        <>
          <table className="campaigns-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Template</th>
                <th>Status</th>
                <th>Audience</th>
                <th>Scheduled</th>
                <th>Delivery</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((campaign) => (
                <tr
                  key={campaign.id}
                  onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  className="campaign-row"
                >
                  <td className="campaign-name">{campaign.name}</td>
                  <td className="campaign-template">
                    {campaign.template_name || '—'}
                    {campaign.template_category && (
                      <span className="template-category"> ({campaign.template_category})</span>
                    )}
                    {shouldWarnInactiveTemplate(campaign) && (
                      <span className="template-inactive" title={INACTIVE_TEMPLATE_MESSAGE}>
                        template inactive
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(campaign.status)}>
                      {campaign.status}
                    </span>
                  </td>
                  <td className="campaign-audience">
                    {campaign.estimated_audience !== null
                      ? `~${campaign.estimated_audience} recipients`
                      : '—'}
                  </td>
                  <td className="campaign-scheduled">
                    {formatDate(campaign.schedule_at)}
                  </td>
                  <td className="campaign-delivery">
                    {campaign.status === 'sending' || campaign.status === 'sent' ? (
                      <div className="delivery-info">
                        <span className="delivery-count">
                          {campaign.total_sent}/{campaign.total_recipients}
                        </span>
                        <div className="delivery-progress">
                          <div
                            className="delivery-progress-bar"
                            style={{
                              width: `${(campaign.total_sent / campaign.total_recipients) * 100}%`
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="campaign-created">
                    {formatRelativeTime(campaign.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {data && data.total > 20 && (
            <div className="pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary"
              >
                Previous
              </button>
              <span className="page-info">
                Page {page} of {Math.ceil(data.total / 20)}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(data.total / 20)}
                className="btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CampaignList;
